import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { Property, PropertyAvailabilityRules } from '@app/shared/interfaces/lib/property.interface';
import { UiModule } from '@app/ui';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { gteProp } from '@app/shared/validators';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { Validators } from '@angular/forms';
import { arrayMax, lteProp } from '@app/shared/validators';
import { Router } from '@angular/router';
import { debounceTime, filter, switchMap, takeUntil } from 'rxjs/operators';
import { dedupeArray } from '@app/shared/utils';
import { ErrorStateMatcher } from '@angular/material/core';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { CommonModule } from '@angular/common';
import { PluralizePipe } from '@app/shared/pipes/pluralize.pipe';

/** Error when invalid control invalid, even before touched */
export class ImmediateErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null): boolean {
    return control.invalid;
  }
}

const PREPARATION_TIME_OPTIONS = [
  { label: 'None', value: 0 },
  { label: '1 turnover night', value: 1 },
  { label: '2 turnover nights', value: 2 },
];

const AVAIALABILITY_WINDOW_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1)
  .map((i) => ({
    label: `${i} months`,
    value: i,
  }))

  .concat([
    { label: '18 months', value: 18 },
    { label: '24 months', value: 24 },
  ]);

// 0-24 hours, then multiples of 24 hours up to 30 days
const ADVANCE_NOTICE_OPTIONS = [
  {
    label: 'Same day',
    value: 1,
  },
].concat(
  [1, 2, 3, 7].map((i) => ({
    label: `At least ${i} day${i > 1 ? 's' : ''}`,
    value: i * 24,
  }))
);

// 6:00 AM - 11:00 PM
const ADVANCE_NOTICE_SAME_DAY_OPTIONS = Array.from({ length: 6 }, (_, i) => ({
  label: `${i + 6}:00 AM`,
  value: 18 - i, // 6am is 18 hours until midnight
}))
  .concat([
    {
      label: '12:00 PM',
      value: 12,
    },
  ])
  .concat(
    Array.from({ length: 11 }, (_, i) => ({
      label: `${i + 1}:00 PM`,
      value: 11 - i, // 4pm is 8 hours until midnight
    }))
  );

@Component({
  selector: 'sbnb-property-availability-rules-form',
  templateUrl: './property-availability-rules-form.component.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [UiModule, FormsModule, ReactiveFormsModule, CommonModule, PluralizePipe],
})
export class PropertyAvailabilityRulesFormComponent implements OnInit, OnDestroy {
  @Input() property!: Property;
  @Output() saved = new EventEmitter<void>();

  private readonly authService = inject(AuthenticationService);
  private readonly propertyService = inject(PropertiesService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastNotificationsService);
  private readonly fb = inject(UntypedFormBuilder);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  public immediateErrorMatcher = new ImmediateErrorStateMatcher();
  public rulesForm;
  public preparationTimeOptions = PREPARATION_TIME_OPTIONS;
  public availabilityWindowOptions = [
    { label: 'Dates unavailable by default', value: 0 },
    ...AVAIALABILITY_WINDOW_OPTIONS,
  ];
  public advanceNoticeOptions = [{ label: 'None', value: 0 }, ...ADVANCE_NOTICE_OPTIONS];
  public advanceNoticeSameDayOptions = ADVANCE_NOTICE_SAME_DAY_OPTIONS;

  public startWeekOnSunday = this.authService.getStartOfWeekPreference() === 0;

  public customizeMinStay$ = new BehaviorSubject<boolean>(false);

  public initialized$ = new BehaviorSubject<boolean>(false);

  public saving$ = new BehaviorSubject<boolean>(false);

  ngOnInit(): void {
    this.initialize();
    this.listenForFormChanges();
  }

  private initialize() {
    // Fetch property availability rules and initialize form
    this.propertyService.getPropertyAvailabilityRules(this.property.id).subscribe((rules) => {
      if (this.propertyService.isSimplifiedError(rules) || !rules) {
        this.toastErrorAndRedirect(`Unable to load property availability rules.`);
        return;
      }

      this.setUpForm(rules);

      if (!this.minStayDaysAllMatch) {
        this.toggleCustomizeMinStay(true);
      }

      if (this.rulesForm.get('max_stay').value < 28) {
        this.rulesForm.get('allow_rtb_above_max_nights').disable();
      }
    });
  }

  private setUpForm(rules: PropertyAvailabilityRules): void {
    const {
      restricted_check_in,
      restricted_check_out,
      preparation_time,
      availability_window,
      max_stay,
      min_stay,
      allow_rtb_above_max_nights,
    } = rules;
    const isSameDay = rules.advance_notice?.default?.hours > 0 && rules.advance_notice?.default?.hours < 24;
    const advance_notice = isSameDay ? 1 : rules.advance_notice?.default?.hours;
    const advance_notice_same_day = isSameDay ? rules.advance_notice?.default?.hours : null;
    /**
     * Form is iniatialized here with its default values
     * so that resetting the form will reset to these values
     * `initialValueIsDefault` is deprecated in NG14 and replaced with `nonNullable` :grimace:
     */
    this.rulesForm = this.fb.group({
      restricted_check_in: [restricted_check_in.default, { validators: [arrayMax(6)], initialValueIsDefault: true }], // array, max of 6 selected
      restricted_check_out: [restricted_check_out.default, { validators: [arrayMax(6)], initialValueIsDefault: true }], // array, max of 6 selected
      preparation_time: [
        preparation_time.default.days,
        { validators: [Validators.required, Validators.max(2)], initialValueIsDefault: true },
      ], // dropdown, 0, 1, 2
      advance_notice: [
        advance_notice,
        { validators: [Validators.required, Validators.max(720)], initialValueIsDefault: true },
      ], // dropdown, number (of hours), Allow 0-24 (hours), then multiples of 24 (24 * [1-30]), max 30x24
      advance_notice_same_day: [advance_notice_same_day, { validators: [], initialValueIsDefault: true }], // dropdown, number (of hours), 1-24
      advance_notice_allow_request_to_book: [
        rules.advance_notice.default.allow_request_to_book,
        { initialValueIsDefault: true },
      ], // optionally revealed when advance_notice value is > 0 and host has instant book enabled
      availability_window: [
        availability_window.default.months,
        { validators: [Validators.required, Validators.max(24)], initialValueIsDefault: true },
      ], // dropdown, number (of months), max 24
      max_stay: [
        max_stay.default.days,
        {
          validators: [Validators.required, Validators.max(1125), gteProp('min_stay')],
          initialValueIsDefault: true,
        },
      ], // number (of days), max is 1125
      min_stay: [
        min_stay.default.days,
        {
          validators: [Validators.required, Validators.max(1125), lteProp('max_stay')],
          initialValueIsDefault: true,
        },
      ],
      min_stay_days: this.fb.array(
        min_stay.default.custom_days.map((day) => {
          return this.fb.control(day, {
            validators: [Validators.max(1125), lteProp('max_stay')],
            initialValueIsDefault: true,
          });
        })
      ),
      // number (of days), max is 1125
      allow_rtb_above_max_nights: [
        allow_rtb_above_max_nights.default.allow_rtb_above_max_nights,
        { validators: [Validators.max(1125)], initialValueIsDefault: true },
      ],
    });
    this.initialized$.next(true);
  }

  private listenForFormChanges(): void {
    // Set child field values based on advance_notice value
    this.initialized$
      .pipe(
        filter((value) => Boolean(value)),
        switchMap(() => this.rulesForm.get('advance_notice').valueChanges),
        debounceTime(500),
        takeUntil(this.destroyed$)
      )
      .subscribe((value) => this.setAdvanceNotice(value));

    this.initialized$
      .pipe(
        filter((value) => Boolean(value)),

        switchMap(() => this.rulesForm.get('max_stay').valueChanges),
        debounceTime(1000),
        takeUntil(this.destroyed$)
      )
      .subscribe((value) => this.setRequestToBook(value));

    // If min stay changes, and the user has not customized the min stay days, set the min stay days to the new value
    this.initialized$
      .pipe(
        filter((value) => Boolean(value)),
        switchMap(() => this.rulesForm.get('min_stay').valueChanges),
        debounceTime(1000),
        takeUntil(this.destroyed$)
      )
      .subscribe((value) => {
        if (this.rulesForm.get('min_stay_days').touched || !this.minStayDaysAllMatch) {
          return;
        }
        this.rulesForm.get('min_stay_days').controls.forEach((control) => {
          control.setValue(value);
        });
      });
  }

  private get minStayDaysAllMatch(): boolean {
    return dedupeArray(this.rulesForm.get('min_stay_days').value)?.length === 1;
  }

  private setRequestToBook(value): void {
    if (value >= 28) {
      this.rulesForm?.get('allow_rtb_above_max_nights').enable();
      return;
    }
    this.rulesForm?.get('allow_rtb_above_max_nights').setValue(false);
    this.rulesForm?.get('allow_rtb_above_max_nights').disable();
  }

  private setAdvanceNotice(value): void {
    if (value === 0) {
      this.rulesForm?.get('advance_notice_allow_request_to_book').setValue(false);
    }

    if (value === 1) {
      this.rulesForm.get('advance_notice_same_day').setValue(8);
      this.rulesForm.get('advance_notice_same_day').setValidators([Validators.required]);
    } else {
      this.rulesForm.get('advance_notice_same_day').setValidators([]);
      this.rulesForm.get('advance_notice_same_day').setValue(null);
    }
  }

  public get minStayDaysLabels(): string[] {
    return this.startWeekOnSunday
      ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  }

  // Adjust the order of display according to the user's preference,
  // but keep the original form order the same to be sent to the server
  public get minStayDaysPreferred() {
    return this.startWeekOnSunday
      ? this.rulesForm.get('min_stay_days').controls
      : this.rulesForm.get('min_stay_days').controls.slice(1).concat(this.rulesForm.get('min_stay_days').controls[0]);
  }

  public toggleCustomizeMinStay(value: boolean): void {
    this.customizeMinStay$.next(value);
    this.rulesForm.markAsDirty();
  }

  public reset(): void {
    this.rulesForm.reset();
  }

  public save(): void {
    this.saving$.next(true);
    const propertyId = this.property.id;
    const { advance_notice, advance_notice_same_day } = this.rulesForm.getRawValue(); // includes disabled fields
    if (advance_notice === 1) {
      this.rulesForm.get('advance_notice').setValue(advance_notice_same_day);
    }
    const values = { ...this.rulesForm.getRawValue() }; // includes disabled fields };
    delete values.advance_notice_same_day;
    if (this.minStayDaysAllMatch || !this.customizeMinStay$.value) {
      delete values.min_stay_days;
    }

    this.propertyService.updatePropertyAvailabilityRules(propertyId, values).subscribe((res) => {
      this.saving$.next(false);
      if (this.propertyService.isSimplifiedError(res)) {
        this.toastError(res.message);
        return;
      }
      this.toastSuccess();
      this.rulesForm.markAsPristine();
      this.saved.emit();
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(null);
    this.destroyed$.complete();
  }

  private toastError(message): void {
    this.toast.error(`Failed to save property availability rules: ${message}`);
  }

  private toastSuccess(): void {
    this.toast.success('Property availability rules saved.');
  }

  private toastErrorAndRedirect(message): void {
    this.toast.error(message);
    this.router.navigate(['/properties']);
  }
}
